import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface UserInfo {
  username: string;
  email: string;
  hwid?: string;
  products: {
    id: number;
    name: string;
    expiryDate?: string;
    periodEnd?: string;
  }[];
}

interface AuthContextType {
  isAuthenticated: boolean;
  userInfo: UserInfo | null;
  isLoading: boolean;
  error: string | null;
  logout: () => void;
  fetchUserInfo: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUserInfo = async () => {
    try {
      const response = await fetch('/tryGetUserInfo');
      const data = await response.json();
      
      if (data.code === 200) {
        setUserInfo(data.userInfo);
        setIsAuthenticated(true);
      } else {
        setError(data.error || 'Failed to load user information');
        setIsAuthenticated(false);
        setUserInfo(null);
      }
    } catch (err) {
      console.error('Error fetching user info:', err);
      setError('Failed to connect to the server');
      setIsAuthenticated(false);
      setUserInfo(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      const sessionCookie = document.cookie.split('; ').find(row => row.startsWith('session='));
      
      if (sessionCookie) {
        await fetchUserInfo();
      } else {
        setIsAuthenticated(false);
        setUserInfo(null);
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const logout = () => {
    document.cookie = 'session=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    setIsAuthenticated(false);
    setUserInfo(null);
    navigate('/login');
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, userInfo, isLoading, error, logout, fetchUserInfo }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 